
import React, { useEffect, useState, useMemo } from 'react';
import { CartItem, UserAddress, ShippingMethod, PaymentMethod, isProductPreSale } from '../../types';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { supabase } from '../../lib/supabase';
import { 
  getAddresses, 
  getStoreShippingMethods, 
  getStorePaymentMethods, 
  createOrderFull,
  checkUserDocument,
  isStoreOpen
} from '../../services/supabaseService';
import { useNotification } from '../../components/member/NotificationProvider';
import { CheckoutModal } from '../../components/cart/CheckoutModal';

interface CartPageProps {
  cart: CartItem[];
  updateQuantity: (id: string, delta: number) => void;
  removeFromCart: (id: string) => void;
  clearCart: (storeId?: string | number) => void;
  addToCart?: (product: any) => void;
}

const formatWeight = (grams: number | null | undefined): string => {
  const g = grams || 0;
  if (g >= 1000) {
    return `${(g / 1000).toFixed(2)} kg`;
  }
  return `${g.toFixed(0)}g`;
};

const isPickupMethod = (method: any): boolean => {
  if (!method) return false;
  if (Number(method.id) === 4 || Number(method.shipping_method_id) === 4) return true;
  const name = (method.name || '').toLowerCase();
  const service = (method.service_type || '').toLowerCase();
  return (
    name.includes('retirada') || 
    name.includes('balcão') || 
    name.includes('balcao') || 
    name.includes('loja') || 
    name.includes('local') ||
    service.includes('pickup') ||
    service.includes('retirada')
  );
};

const getStorePhysicalAddress = (storeInfo: any): string => {
  if (!storeInfo) return 'Balcão da Loja Física';
  const parts: string[] = [];
  if (storeInfo.street) {
    parts.push(`${storeInfo.street}${storeInfo.number ? `, ${storeInfo.number}` : ', S/N'}`);
  }
  if (storeInfo.complement) {
    parts.push(storeInfo.complement);
  }
  if (storeInfo.neighborhood) {
    parts.push(storeInfo.neighborhood);
  }
  if (storeInfo.city) {
    const st = storeInfo.state?.code || storeInfo.estado;
    parts.push(`${storeInfo.city}${st ? ` - ${st}` : ''}`);
  }
  if (storeInfo.cep) {
    parts.push(`CEP: ${storeInfo.cep}`);
  }
  return parts.length > 0 ? parts.join(' • ') : 'Balcão da Loja Física';
};

interface CheckResult {
  exceeded: boolean;
  reason: string;
}

const checkPackageLimits = (
  methodName: string,
  totalWeightKg: number,
  height: number,
  width: number,
  length: number
): CheckResult => {
  const nameLower = methodName.toLowerCase();
  
  if (nameLower.includes('carta')) {
    if (totalWeightKg > 0.5) {
      return { exceeded: true, reason: 'Peso máximo para Carta Registrada é 500g (excedido).' };
    }
    if (height > 2) {
      return { exceeded: true, reason: 'Espessura máxima para Carta Registrada é 2cm (excedida).' };
    }
    if (length > 35 || width > 25) {
      return { exceeded: true, reason: 'Dimensão máxima para Carta Registrada é 35x25cm (excedida).' };
    }
  } else if (nameLower.includes('mini') && nameLower.includes('envio')) {
    if (totalWeightKg > 0.35) {
      return { exceeded: true, reason: 'Peso máximo para Correios Mini Envios é 350g (excedido).' };
    }
    if (height > 4) {
      return { exceeded: true, reason: 'Espessura máxima para Correios Mini Envios é 4cm (excedida).' };
    }
    if (length > 35 || width > 25) {
      return { exceeded: true, reason: 'Dimensão máxima para Correios Mini Envios é 35x25cm (excedida).' };
    }
  } else {
    if (totalWeightKg > 30) {
      return { exceeded: true, reason: 'Peso máximo para encomenda padrão é de 30kg (excedido).' };
    }
    if (length > 100 || width > 100 || height > 100) {
      return { exceeded: true, reason: 'Nenhuma dimensão individual pode exceder 100cm.' };
    }
    if (length + width + height > 200) {
      return { exceeded: true, reason: 'Limite de soma das dimensões das embalagens padrão ultrapassou 200cm.' };
    }
  }
  
  return { exceeded: false, reason: '' };
};

export const CartPage: React.FC<CartPageProps> = ({ cart, updateQuantity, removeFromCart, clearCart, addToCart }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  // Lojista detection for quick navigation banner
  const isLojista = user?.role_id === 6 || user?.role_id === 1;
  const isOrderingFromOwnStore = false;
  const myStore: any = null;

  // Checkout Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState<string | number | null>(null);
  
  // Delivery mode per store ('pickup' by default, or 'delivery' if requested)
  const [storeDeliveryModes, setStoreDeliveryModes] = useState<Record<string, 'pickup' | 'delivery'>>({});
  const currentStoreKey = selectedStoreId ? selectedStoreId.toString() : '';
  const currentDeliveryMode: 'pickup' | 'delivery' = storeDeliveryModes[currentStoreKey] || 'pickup';

  const [addresses, setAddresses] = useState<UserAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string | number | null>(null);
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
  const [selectedShippingId, setSelectedShippingId] = useState<number | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | string | null>(null);
  const [isStoreCurrentlyOpen, setIsStoreCurrentlyOpen] = useState<boolean | null>(null);
  const [isCheckingStoreOpen, setIsCheckingStoreOpen] = useState(false);
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasDocument, setHasDocument] = useState<boolean | null>(null);

  // Melhor Envio state hooks
  const [storesDetailedInfo, setStoresDetailedInfo] = useState<Record<string, any>>({});
  const [storesShippingMethods, setStoresShippingMethods] = useState<Record<string, ShippingMethod[]>>({});
  const [customCep, setCustomCep] = useState('');
  const [isLoadingFreight, setIsLoadingFreight] = useState(false);
  const [freightResults, setFreightResults] = useState<Record<string, any[]>>({});
  const [selectedFreights, setSelectedFreights] = useState<Record<string, any>>({});

  // Track cart alterations for freight recalculation
  const [lastCalculatedCartFingerprint, setLastCalculatedCartFingerprint] = useState<string | null>(null);

  const cartFingerprint = useMemo(() => {
    return cart.map(item => `${item.id}-${item.quantity}`).join(',');
  }, [cart]);

  const isCartAltered = useMemo(() => {
    if (Object.keys(freightResults).length === 0) return false; // Freight hasn't been calculated yet
    return lastCalculatedCartFingerprint !== null && cartFingerprint !== lastCalculatedCartFingerprint;
  }, [cartFingerprint, lastCalculatedCartFingerprint, freightResults]);

  const total = cart.reduce((acc, item) => acc + ((item.price || 0) * (item.quantity || 0)), 0);

  const freightTotal = useMemo(() => {
    return Object.values(selectedFreights).reduce((sum, rate) => sum + (Number(rate.custom_price ?? rate.price) || 0), 0);
  }, [selectedFreights]);

  // Shifting selectedStoreFreightPrice below getProcessedRatesForStore to avoid hoisting issues

  const cartByStore = useMemo(() => {
    return cart.reduce((groups, item) => {
      const storeId = item.storeId || item.store_id || 'unknown';
      const storeName = item.storeName || 'Marketplace';
      if (!groups[storeId]) {
        groups[storeId] = { id: storeId, name: storeName, items: [], total: 0 };
      }
      groups[storeId].items.push(item);
      groups[storeId].total += (Number(item.price) || 0) * (item.quantity || 1);
      return groups;
    }, {} as Record<string, { id: string | number, name: string, items: CartItem[], total: number }>);
  }, [cart]);

  const storesInCart = useMemo(() => {
    return Object.values(cartByStore);
  }, [cartByStore]);

  const activeStoreGroup = useMemo(() => {
    return selectedStoreId ? cartByStore[selectedStoreId.toString()] : null;
  }, [selectedStoreId, cartByStore]);

  // Fetch detailed stores info and shipping methods
  useEffect(() => {
    const fetchStoresInfo = async () => {
      const ids = storesInCart
        .map(s => s.id)
        .filter(id => id !== 'unknown' && id !== 'generic' && !isNaN(Number(id)))
        .map(id => Number(id));
      if (ids.length === 0) return;
      try {
        const { data, error } = await supabase
          .from('stores')
          .select('*, state:states(code)')
          .in('id', ids);
        if (data) {
          const infoMap: Record<string, any> = {};
          data.forEach(s => {
            infoMap[s.id.toString()] = s;
          });
          setStoresDetailedInfo(infoMap);
        }
      } catch (err) {
        console.error("Error fetching stores details:", err);
      }
    };

    const fetchAllShippingMethods = async () => {
      const ids = storesInCart
        .map(s => s.id)
        .filter(id => id !== 'unknown' && id !== 'generic');
      if (ids.length === 0) return;
      
      const methodsMap: Record<string, ShippingMethod[]> = {};
      await Promise.all(
        ids.map(async (id) => {
          try {
            const methods = await getStoreShippingMethods(id);
            methodsMap[id.toString()] = methods;
          } catch (e) {
            console.error("Error fetching shipping methods for store " + id, e);
          }
        })
      );
      setStoresShippingMethods(methodsMap);
    };

    fetchStoresInfo();
    fetchAllShippingMethods();
  }, [cart]);

  const buyerAddress = useMemo(() => {
    return addresses.find(a => a.id === selectedAddressId);
  }, [addresses, selectedAddressId]);

  const hasCalculatedAllFreights = useMemo(() => {
    return storesInCart.every(s => freightResults[s.id.toString()] && freightResults[s.id.toString()].length > 0);
  }, [storesInCart, freightResults]);

  const isActiveStoreSalesDisabled = useMemo(() => {
    if (!selectedStoreId) return false;
    return storesDetailedInfo[selectedStoreId.toString()]?.sales_enabled === false;
  }, [selectedStoreId, storesDetailedInfo]);

  const getProcessedRatesForStore = (storeGroupIdForCheck: string) => {
    const rawRates = freightResults[storeGroupIdForCheck] || [];
    const storeMethods = storesShippingMethods[storeGroupIdForCheck] || [];
    
    // Calculate total weight and dimensions for this store
    let totalWeight = 0;
    let maxHeight = 0;
    let maxWidth = 0;
    let maxLength = 0;
    
    const storeGroup = cartByStore[storeGroupIdForCheck];
    if (storeGroup) {
      storeGroup.items.forEach(item => {
        const wtInGrams = Number(item.weight) || 0;
        const wtInKg = wtInGrams / 1000;
        totalWeight += wtInKg * item.quantity;
        maxHeight += (item.quantity * 2);
        maxWidth = Math.max(maxWidth, 12);
        maxLength = Math.max(maxLength, 16);
      });
    }

    const storeMelhorEnvioExternalIds = storeMethods
      .filter(m => m.external_id !== null && m.external_id !== undefined && m.is_better_envio !== false && m.source !== 'manual')
      .map(m => Number(m.external_id));

    // Process rates returned by the API
    const processedApiRates = rawRates.map(rate => {
      const isEnabledByStore = storeMelhorEnvioExternalIds.includes(Number(rate.id));
      
      const check = checkPackageLimits(rate.name, totalWeight, maxHeight, maxWidth, maxLength);
      const isWeightExceeded = check.exceeded;
      const limitReason = check.reason;

      return {
        ...rate,
        isFromApi: true,
        isEnabledByStore,
        isWeightExceeded,
        limitReason,
        isSelectable: isEnabledByStore && !isWeightExceeded,
        isManual: false
      };
    });

    const manualMethods = storeMethods.filter(method => {
      return method.is_better_envio === false || method.source === 'manual' || method.external_id === null || method.external_id === undefined;
    });

    const processedManualRates = manualMethods.map(method => {
      return {
        id: method.id,
        name: method.name,
        price: method.price || 0,
        custom_price: method.price || 0,
        delivery_time: method.delivery_days || method.estimated_days || 5,
        company: {
          name: method.company_name || 'Transportadora',
          picture: ''
        },
        isFromApi: false,
        isEnabledByStore: true,
        isWeightExceeded: false,
        limitReason: '',
        isSelectable: true,
        isManual: true
      };
    });

    // Determine missing configured store methods that the API did not return
    const missingMethods = storeMethods.filter(method => {
      if (method.external_id === null || method.external_id === undefined) return false;
      if (method.source === 'manual' || method.is_better_envio === false) return false;
      return !rawRates.some(rate => Number(rate.id) === Number(method.external_id));
    });

    const processedVirtualRates = missingMethods.map(method => {
      const check = checkPackageLimits(method.name, totalWeight, maxHeight, maxWidth, maxLength);
      const isWeightExceeded = check.exceeded;
      const limitReason = isWeightExceeded ? check.reason : 'Serviço indisponível ou fora dos limites do endereço';

      return {
        id: Number(method.external_id),
        name: method.name,
        price: method.price || 0,
        custom_price: method.price || 0,
        delivery_time: method.delivery_days || 5,
        company: {
          name: method.company_name || 'Transportadora',
          picture: ''
        },
        isFromApi: false,
        isEnabledByStore: true,
        isWeightExceeded,
        limitReason,
        isSelectable: false,
        isManual: false
      };
    });

    const allRates = [...processedApiRates, ...processedManualRates, ...processedVirtualRates].filter(rate => rate.isEnabledByStore);
    
    // Sort so that selectable rates come first, then by price
    return allRates.sort((a, b) => {
      if (a.isSelectable && !b.isSelectable) return -1;
      if (!a.isSelectable && b.isSelectable) return 1;
      return (Number(a.custom_price ?? a.price) || 0) - (Number(b.custom_price ?? b.price) || 0);
    });
  };

  const selectedStoreFreightPrice = useMemo(() => {
    if (!selectedStoreId) return 0;
    if (isOrderingFromOwnStore) return 0;
    if (currentDeliveryMode === 'pickup') return 0;
    if (!selectedShippingId) return 0;
    const method = shippingMethods.find(m => m.id === selectedShippingId);
    if (!method) return 0;
    if (isPickupMethod(method)) return 0;

    const rates = getProcessedRatesForStore(selectedStoreId.toString());
    const foundRate = rates.find(r => {
      if (r.isManual) {
        return r.id === method.id;
      }
      if (method.external_id && Number(r.id) === Number(method.external_id)) {
        return true;
      }
      return r.name.toLowerCase() === method.name.toLowerCase();
    });

    if (foundRate) {
      return Number(foundRate.custom_price ?? foundRate.price) || 0;
    }
    return method.price || 0;
  }, [selectedStoreId, selectedShippingId, shippingMethods, freightResults, storesShippingMethods, isOrderingFromOwnStore, currentDeliveryMode]);

  const isCashMethod = (method: PaymentMethod | null | undefined): boolean => {
    if (!method) return false;
    const nameLower = method.name?.toLowerCase() || '';
    const slugLower = method.slug?.toLowerCase() || '';
    return (
      slugLower === 'cash' ||
      slugLower === 'dinheiro' ||
      nameLower.includes('dinheiro') ||
      nameLower.includes('cash') ||
      method.requires_online === false
    );
  };

  const activeStoreOrderTotal = useMemo(() => {
    const storeItemsTotal = activeStoreGroup?.total || 0;
    const shipping = (currentDeliveryMode === 'pickup' || isOrderingFromOwnStore) ? 0 : selectedStoreFreightPrice;
    return storeItemsTotal + shipping;
  }, [activeStoreGroup?.total, currentDeliveryMode, isOrderingFromOwnStore, selectedStoreFreightPrice]);

  const isCartTotalZero = useMemo(() => {
    if (activeStoreGroup) {
      return activeStoreOrderTotal <= 0;
    }
    return total <= 0;
  }, [activeStoreGroup, activeStoreOrderTotal, total]);

  const effectivePaymentMethods = useMemo(() => {
    if (isCartTotalZero) {
      const cashList = paymentMethods.filter(isCashMethod);
      if (cashList.length > 0) {
        return cashList;
      }
      return [
        {
          id: 4,
          name: 'Dinheiro',
          slug: 'cash',
          type: 'cash',
          requires_online: false,
          description: 'Pagamento presencial em dinheiro / Pedido gratuito'
        }
      ];
    }
    return paymentMethods;
  }, [isCartTotalZero, paymentMethods]);

  // Keep payment method synchronized; enforce cash if total is 0
  useEffect(() => {
    if (effectivePaymentMethods.length > 0) {
      const isValid = effectivePaymentMethods.some(m => m.id === selectedPaymentId);
      if (!isValid) {
        setSelectedPaymentId(effectivePaymentMethods[0].id);
      }
    } else if (!isOrderingFromOwnStore) {
      setSelectedPaymentId(null);
    }
  }, [effectivePaymentMethods, selectedPaymentId, isOrderingFromOwnStore]);

  useEffect(() => {
    if (buyerAddress?.cep) {
      setCustomCep(buyerAddress.cep);
    }
  }, [buyerAddress]);

  useEffect(() => {
    setFreightResults({});
    setSelectedFreights({});
  }, [selectedAddressId]);

  const handleCalculateAllFreights = async () => {
    const activeAddressCep = buyerAddress?.cep || customCep;
    const cleanTo = activeAddressCep.replace(/\D/g, '');
    if (cleanTo.length !== 8) {
      showNotification("Por favor, selecione um endereço de entrega válido.", "info");
      return;
    }

    setIsLoadingFreight(true);
    const results: Record<string, any[]> = {};
    
    try {
      // Loop through all stores in the cart in parallel
      const promises = storesInCart.map(async (storeGroup) => {
        const storeInfo = storesDetailedInfo[storeGroup.id.toString()];
        const rawOriginCep = storeInfo?.contact?.cep || 
                             storeInfo?.contato?.cep || 
                             storeInfo?.cep || 
                             storeInfo?.contact?.postal_code || 
                             storeInfo?.contato?.postal_code || 
                             storeInfo?.address?.cep || 
                             storeInfo?.address?.postal_code || 
                             storeInfo?.endereco?.cep;
        
        if (!rawOriginCep) {
          throw new Error(`A loja "${storeGroup.name}" não possui um CEP de origem cadastrado nas configurações dela.`);
        }

        const cleanFrom = rawOriginCep.replace(/\D/g, '');
        if (cleanFrom.length !== 8) {
          throw new Error(`O CEP de origem cadastrado da loja "${storeGroup.name}" ("${rawOriginCep}") é inválido.`);
        }

        // Calculate package volume heuristics safely
        let totalWeight = 0;
        let maxHeight = 0;
        let maxWidth = 0;
        let maxLength = 0;

        storeGroup.items.forEach(item => {
          const wtInGrams = Number(item.weight) || 0;
          const wtInKg = wtInGrams / 1000;
          totalWeight += wtInKg * item.quantity;
          maxHeight += (item.quantity * 2);
          maxWidth = Math.max(maxWidth, 12);
          maxLength = Math.max(maxLength, 16);
        });

        const productsList = storeGroup.items.map(item => {
          const wtInGrams = Number(item.weight) || 0;
          const wtInKg = wtInGrams / 1000;
          return {
            id: item.id.toString(),
            quantity: item.quantity,
            weight: Math.max(0.01, parseFloat(wtInKg.toFixed(3))),
            width: Math.max(10, maxWidth),
            height: Math.max(4, maxHeight),
            length: Math.max(15, maxLength),
            insurance_value: 0
          };
        });

        const payload = {
          from: { postal_code: cleanFrom },
          to: { postal_code: cleanTo },
          products: productsList
        };

        console.log("Melhor Envio calculate payload: ", JSON.stringify(payload, null, 2));

        const response = await fetch('/api/melhorenvio/calculate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData?.error || `Erro de cotação no servidor (${response.status}). Certifique-se de que a variável de ambiente VITE_MELHOR_ENVIO_TOKEN está preenchida.`);
        }

        const data = await response.json();

        if (Array.isArray(data)) {
          results[storeGroup.id.toString()] = data;

          const storeMethods = storesShippingMethods[storeGroup.id.toString()] || [];
          const storeMelhorEnvioExternalIds = storeMethods
            .filter(m => m.external_id !== null && m.external_id !== undefined)
            .map(m => Number(m.external_id));

          // Try to select the first option returned that is both enabled by the store and within weight/dimension limits
          const firstSelectableRate = data.find((rate: any) => {
            const isEnabled = storeMelhorEnvioExternalIds.includes(Number(rate.id));
            const check = checkPackageLimits(rate.name, totalWeight, maxHeight, maxWidth, maxLength);
            return isEnabled && !check.exceeded;
          });

          // Fallback if none is perfectly selectable
          const selectedRate = firstSelectableRate || data[0];

          if (selectedRate) {
            setSelectedFreights(prev => ({
              ...prev,
              [storeGroup.id.toString()]: selectedRate
            }));

            const matchingMethod = storeMethods.find(m => Number(m.external_id) === Number(selectedRate.id));
            if (matchingMethod) {
              setSelectedShippingId(matchingMethod.id);
            }
          }
        }
      });

      await Promise.all(promises);
      setFreightResults(results);
      setLastCalculatedCartFingerprint(cartFingerprint);
      showNotification("Cotação de fretes realizada para todas as lojas!", "success");
    } catch (err: any) {
      console.error("Erro geral no calculo de frete:", err);
      showNotification(err.message || "Erro ao calcular fretes das lojas", "error");
    } finally {
      setIsLoadingFreight(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      checkUserDocument(user.id).then(doc => {
        setHasDocument(!!doc);
      });
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      getAddresses().then(data => {
        setAddresses(data || []);
        const primary = data?.find((a: any) => a.is_default);
        if (primary) setSelectedAddressId(prev => prev || primary.id);
      });
    }
  }, [user?.id]);

  const storesInCartKey = useMemo(() => {
    return storesInCart.map(s => s.id).join(',');
  }, [storesInCart]);

  // Keep selectedStoreId in sync with stores in the cart
  useEffect(() => {
    if (storesInCart.length > 0) {
      if (!selectedStoreId || !storesInCart.some(s => s.id === selectedStoreId)) {
        setSelectedStoreId(storesInCart[0].id);
      }
    } else {
      setSelectedStoreId(null);
    }
  }, [storesInCartKey, selectedStoreId]);

  // Fetch store-specific methods when store is selected
  useEffect(() => {
    if (!selectedStoreId || selectedStoreId === 'unknown') {
      setIsStoreCurrentlyOpen(null);
      setShippingMethods([]);
      setPaymentMethods([]);
      setSelectedShippingId(null);
      setSelectedPaymentId(null);
      return;
    }

    let isMounted = true;

    getStoreShippingMethods(selectedStoreId).then(methods => {
      if (!isMounted) return;
      setShippingMethods(methods);
      
      const mode = storeDeliveryModes[selectedStoreId.toString()] || 'pickup';
      if (mode === 'pickup') {
        const pickupMethod = methods.find(isPickupMethod);
        setSelectedShippingId(pickupMethod?.id || 4);
      } else {
        // Auto-select shipping option matching calculated freight if exists
        const selectedRate = selectedFreights[selectedStoreId.toString()];
        if (selectedRate) {
          const matchingMethod = methods.find((m: any) => {
            if (selectedRate.isManual) {
              return m.id === selectedRate.id; // both match shipping_method_id
            }
            return Number(m.external_id) === Number(selectedRate.id);
          });
          if (matchingMethod) {
            setSelectedShippingId(matchingMethod.id);
          }
        }
      }
    });
    
    getStorePaymentMethods(selectedStoreId).then(methods => {
      if (!isMounted) return;
      setPaymentMethods(methods);
      // Auto-select first payment method if none selected or if previously selected is invalid for new store
      if (methods.length > 0) {
        setSelectedPaymentId(prev => {
          const exists = methods.some(m => m.id === prev);
          return exists ? prev : methods[0].id;
        });
      } else {
        setSelectedPaymentId(null);
      }
    });
    
    setIsCheckingStoreOpen(true);
    isStoreOpen(selectedStoreId)
      .then(res => {
        if (!isMounted) return;
        setIsStoreCurrentlyOpen(res);
      })
      .finally(() => {
        if (isMounted) {
          setIsCheckingStoreOpen(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [selectedStoreId, isOrderingFromOwnStore]);

  const handleOpenCheckout = () => {
    if (!user) {
      showNotification("Faça login para finalizar o pedido", "error");
      return;
    }

    if (isOrderingFromOwnStore) {
      setIsModalOpen(true);
      return;
    }

    if (isActiveStoreSalesDisabled) {
      showNotification("As vendas estão desabilitadas para esta loja no momento.", "error");
      return;
    }

    if (!selectedStoreId) {
      showNotification("Selecione uma loja para continuar.", "info");
      return;
    }

    const currentPaymentId = isCartTotalZero 
      ? (selectedPaymentId || effectivePaymentMethods[0]?.id || 4)
      : selectedPaymentId;

    if (!currentPaymentId) {
      showNotification("Por favor, selecione uma forma de pagamento para continuar.", "info");
      return;
    }

    if (currentDeliveryMode === 'delivery') {
      if (!selectedAddressId) {
        showNotification("Por favor, selecione um endereço de entrega para receber em casa.", "info");
        return;
      }
      if (!selectedShippingId) {
        showNotification("Por favor, calcule e selecione uma opção de frete.", "info");
        return;
      }
      if (isCartAltered) {
        showNotification("O carrinho foi alterado. Por favor, calcule o frete novamente antes de prosseguir.", "info");
        return;
      }
    }

    if (hasDocument === false) {
      setIsModalOpen(true);
      return;
    }

    setIsModalOpen(true);
  };

  const handleProcessCheckout = async () => {
    if (!selectedStoreId) {
      showNotification("Selecione uma loja", "info");
      return;
    }

    const finalPaymentId = isCartTotalZero
      ? (effectivePaymentMethods.find(isCashMethod)?.id || effectivePaymentMethods[0]?.id || selectedPaymentId || 4)
      : isOrderingFromOwnStore
        ? (selectedPaymentId || 4)
        : Number(selectedPaymentId);

    if (!finalPaymentId && !isOrderingFromOwnStore) {
      showNotification("Selecione uma forma de pagamento", "info");
      return;
    }

    if (currentDeliveryMode === 'delivery' && !isOrderingFromOwnStore) {
      if (!selectedAddressId || !selectedShippingId) {
        showNotification("Selecione o endereço e a opção de frete para entrega", "info");
        return;
      }
    }

    if (isActiveStoreSalesDisabled && !isOrderingFromOwnStore) {
      showNotification("As vendas estão desabilitadas para esta loja no momento.", "error");
      return;
    }

    setIsProcessing(true);
    try {
      const storeItems = cartByStore[selectedStoreId.toString()].items;
      const isPickup = currentDeliveryMode === 'pickup' || isOrderingFromOwnStore;
      const calculatedCost = isPickup ? 0 : selectedStoreFreightPrice;
      
      const pickupMethod = shippingMethods.find(isPickupMethod);
      const shippingId = isPickup 
        ? (pickupMethod?.id || 4) 
        : (selectedShippingId || 4);

      const paymentId = isCartTotalZero
        ? (effectivePaymentMethods.find(isCashMethod)?.id || effectivePaymentMethods[0]?.id || 4)
        : isOrderingFromOwnStore
          ? (selectedPaymentId || 4)
          : Number(selectedPaymentId);
      const addressId = (!isPickup && selectedAddressId) ? Number(selectedAddressId) : null;

      const res = await createOrderFull({
        store_id: Number(selectedStoreId),
        address_id: addressId,
        shipping_method_id: shippingId,
        payment_method_id: paymentId,
        shipping_cost: calculatedCost,
        items: storeItems.map(i => ({ 
          product_id: Number(i.productId || i.id), 
          quantity: i.quantity,
          price: Number(i.price) || 0
        }))
      });

      showNotification(
        isOrderingFromOwnStore ? "Pedido balcão criado com sucesso!" : "Pedido criado! Redirecionando para pagamento...", 
        "success"
      );
      clearCart(selectedStoreId);
      setIsModalOpen(false);
      navigate(`/pedido/${res.id}`);
    } catch (e: any) {
      showNotification(e.message || "Erro ao processar pedido", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const isCheckoutDisabled = Boolean(
    isProcessing || 
    cart.length === 0 || 
    !selectedStoreId || 
    (!selectedPaymentId && !isCartTotalZero) || 
    (isActiveStoreSalesDisabled && !isOrderingFromOwnStore) || 
    (!isOrderingFromOwnStore && currentDeliveryMode === 'delivery' && (
      !selectedAddressId || 
      !selectedShippingId || 
      isCartAltered
    ))
  );

  return (
    <div className="max-w-4xl mx-auto space-y-5 sm:space-y-8 pb-28 sm:pb-32 lg:pb-20 px-3 sm:px-4">
      {isLojista && (
        <div className="p-3 sm:p-3.5 bg-purple-950/40 border border-purple-500/30 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center space-x-2.5 text-xs text-purple-200">
            <i className="fas fa-store text-purple-400 text-sm shrink-0"></i>
            <span>Você é um lojista. Para registrar pedidos e vendas presenciais na sua loja física, utilize o <strong>Pedido no Balcão</strong>.</span>
          </div>
          <Link
            to="/pedido-balcao"
            className="w-full sm:w-auto px-3.5 py-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-black transition-colors flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer shrink-0"
          >
            <i className="fas fa-cash-register text-xs"></i>
            <span>Ir para Pedido no Balcão</span>
          </Link>
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-white italic tracking-tighter uppercase">
            Meu Carrinho
          </h1>
          {storesInCart.length > 1 && (
            <span className="inline-flex mt-1.5 bg-purple-500/10 text-purple-400 text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full border border-purple-500/20">
              {storesInCart.length} Lojas no Carrinho
            </span>
          )}
        </div>
      </div>

      {cart.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          <div className="lg:col-span-2 space-y-6 sm:space-y-8">
            {isOrderingFromOwnStore && (
              <div className="bg-slate-900/40 border border-emerald-500/30 p-4 sm:p-5 rounded-2xl sm:rounded-3xl space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-xs font-black text-white uppercase tracking-widest flex items-center">
                    <i className="fas fa-shop text-emerald-400 mr-2"></i>
                    Ponto de Retirada (Sua Própria Loja)
                  </h2>
                  <span className="text-[10px] font-black uppercase tracking-widest bg-emerald-500/10 text-emerald-400 px-2.5 py-0.5 rounded-full border border-emerald-500/20">
                    Venda Balcão
                  </span>
                </div>
                <div className="text-xs text-slate-300">
                  <p className="font-bold text-white">{myStore?.name || activeStoreGroup?.name || 'Sua Loja'}</p>
                  <p className="text-slate-400 mt-0.5">{getStorePhysicalAddress(myStore)}</p>
                </div>
              </div>
            )}

            {/* TABS FOR STORES */}
            <div className="space-y-4 sm:space-y-6">
              <div className="flex border-b border-slate-800 gap-1.5 overflow-x-auto no-scrollbar scroll-smooth snap-x pb-px -mx-3 px-3 sm:mx-0 sm:px-0">
                {storesInCart.map((store) => {
                  const isSelected = selectedStoreId === store.id;
                  return (
                    <button
                      key={`store-tab-${store.id}`}
                      onClick={() => setSelectedStoreId(store.id)}
                      className={`px-3.5 sm:px-5 py-2.5 sm:py-3.5 rounded-t-xl sm:rounded-t-2xl font-black text-[10px] sm:text-[11px] uppercase tracking-wider transition-all duration-200 flex items-center space-x-2 shrink-0 border-t border-x -mb-px cursor-pointer snap-start ${
                        isSelected
                          ? 'bg-slate-900/80 border-slate-800 text-purple-400 border-t-purple-500 border-t-2 shadow-sm'
                          : 'bg-transparent border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-900/30'
                      }`}
                    >
                      <i className="fas fa-shop text-[10px]"></i>
                      <span className="max-w-[120px] sm:max-w-none truncate">{store.name}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-black ${
                        isSelected ? 'bg-purple-500/20 text-purple-400' : 'bg-slate-800/80 text-slate-400'
                      }`}>
                        {store.items.length}
                      </span>
                    </button>
                  );
                })}
              </div>

              {activeStoreGroup && (
                <div className="bg-slate-900/40 border border-slate-800/80 rounded-2xl sm:rounded-3xl p-3.5 sm:p-6 space-y-4 sm:space-y-6">
                  {/* Store Header Info with Store Items count and Limpar Carrinho */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 sm:pb-4 border-b border-slate-800/60">
                    <div className="flex items-center justify-between sm:justify-start gap-2.5 min-w-0">
                      {activeStoreGroup.id !== 'unknown' && activeStoreGroup.id !== 'generic' ? (
                        <Link 
                          to={`/loja/${activeStoreGroup.id}`}
                          className="flex items-center space-x-2 group/store hover:opacity-90 transition-all min-w-0"
                        >
                          <div className="w-8 h-8 rounded-lg bg-purple-600/20 flex items-center justify-center border border-purple-500/20 group-hover/store:border-purple-500/50 transition-colors shrink-0">
                            <i className="fas fa-shop text-xs text-purple-400"></i>
                          </div>
                          <span className="text-xs sm:text-sm font-black text-white group-hover/store:text-purple-400 transition-colors uppercase tracking-wider flex items-center gap-1.5 truncate">
                            <span className="truncate max-w-[130px] sm:max-w-xs">{activeStoreGroup.name}</span>
                            <i className="fas fa-arrow-up-right-from-square text-[9px] text-slate-500 group-hover/store:text-purple-400 transition-colors shrink-0"></i>
                          </span>
                        </Link>
                      ) : (
                        <div className="flex items-center space-x-2 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-purple-600/20 flex items-center justify-center border border-purple-500/20 shrink-0">
                            <i className="fas fa-shop text-xs text-purple-400"></i>
                          </div>
                          <span className="text-xs sm:text-sm font-black text-white uppercase tracking-wider truncate">{activeStoreGroup.name}</span>
                        </div>
                      )}

                      <span className="bg-slate-800/80 px-2.5 py-1 rounded-lg border border-slate-700/80 text-slate-300 text-xs font-bold shrink-0">
                        {activeStoreGroup.items.length} {activeStoreGroup.items.length === 1 ? 'item' : 'itens'}
                      </span>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-3.5 shrink-0">
                      <div className="text-left sm:text-right">
                        <span className="text-xs sm:text-sm font-black text-emerald-400 block font-mono">
                          R$ {activeStoreGroup.total.toFixed(2)}
                        </span>
                        <span className="text-[9px] text-slate-500 font-mono mt-0.5 block">
                          Peso: {formatWeight(activeStoreGroup.items.reduce((acc: number, item: any) => acc + (Number(item.weight) || 0) * (item.quantity || 1), 0))}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          clearCart(activeStoreGroup.id);
                          showNotification(`Itens de ${activeStoreGroup.name} removidos!`, "success");
                        }}
                        className="bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 border border-red-500/20 px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-bold transition-all active:scale-95 flex items-center space-x-1.5 cursor-pointer shrink-0"
                        title={`Remover itens de ${activeStoreGroup.name}`}
                      >
                        <i className="fas fa-trash-can text-[11px]"></i>
                        <span>Limpar</span>
                      </button>
                    </div>
                  </div>

                  {isActiveStoreSalesDisabled && (
                    <div className="bg-rose-500/10 border border-rose-500/20 text-rose-400 p-3.5 sm:p-4 rounded-xl sm:rounded-2xl flex items-start gap-2.5 text-xs">
                      <i className="fas fa-circle-exclamation shrink-0 mt-0.5 text-sm"></i>
                      <div>
                        <p className="font-black uppercase tracking-wider">Vendas Desabilitadas</p>
                        <p className="text-slate-300 leading-normal mt-0.5">Esta loja está com as vendas temporariamente desabilitadas pelo lojista. Não é possível realizar compras no momento.</p>
                      </div>
                    </div>
                  )}

                  {/* Items List - Mobile Responsive Layout */}
                  <div className="space-y-2.5 sm:space-y-3">
                    {activeStoreGroup.items.map((item: any, iIdx: number) => (
                      <div 
                        key={`cart-item-${activeStoreGroup.id}-${item.id || iIdx}`} 
                        className="bg-slate-900 border border-slate-800/90 p-3 sm:p-4 rounded-xl sm:rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 group hover:border-slate-700 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden bg-slate-800 shrink-0 border border-slate-800">
                            <img 
                              src={item.imageUrl || (item as any).image_url || 'https://via.placeholder.com/150'} 
                              alt={item.name} 
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <h3 className="font-bold text-white text-xs sm:text-sm truncate max-w-full">{item.name}</h3>
                              {(item.pre_sale || isProductPreSale(item)) && (
                                <span className="text-[8px] font-black uppercase px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 flex items-center gap-1">
                                  <i className="fas fa-clock text-[7px] text-amber-400"></i>
                                  <span>Pré-Venda</span>
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <p className="text-xs text-purple-400 font-bold font-mono">
                                R$ {(Number(item.price) || 0).toFixed(2)} / un
                              </p>
                              <span className="text-slate-700 text-xs">•</span>
                              <p className="text-[10px] text-slate-500 font-mono" title="Peso unitário">
                                {formatWeight(Number(item.weight) || 0)}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Quantity Controls & Delete Button */}
                        <div className="flex items-center justify-between sm:justify-end gap-3 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-800/60 font-semibold text-slate-300">
                          <span className="text-[11px] text-slate-400 sm:hidden">
                            Subtotal: <strong className="text-emerald-400 font-mono">R$ {((Number(item.price) || 0) * (item.quantity || 1)).toFixed(2)}</strong>
                          </span>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center bg-slate-950 rounded-lg border border-slate-800">
                              <button 
                                type="button"
                                onClick={() => updateQuantity(item.id, -1)}
                                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 rounded-l-lg transition-colors cursor-pointer active:bg-slate-700"
                                aria-label="Diminuir quantidade"
                              >
                                <i className="fas fa-minus text-[10px]"></i>
                              </button>
                              <span className="w-8 text-center text-xs font-bold text-white font-mono">{item.quantity}</span>
                              <button 
                                type="button"
                                onClick={() => updateQuantity(item.id, 1)}
                                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 rounded-r-lg transition-colors cursor-pointer active:bg-slate-700"
                                aria-label="Aumentar quantidade"
                              >
                                <i className="fas fa-plus text-[10px]"></i>
                              </button>
                            </div>
                            <button 
                              type="button"
                              onClick={() => removeFromCart(item.id)}
                              className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-rose-400 hover:bg-rose-950/40 rounded-xl transition-colors cursor-pointer active:scale-95"
                              title="Remover item"
                            >
                              <i className="fas fa-trash-can text-sm"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Shipping / Delivery Selector */}
                  {isOrderingFromOwnStore ? (
                    <div className="bg-slate-950/20 border border-emerald-500/20 p-4 sm:p-5 rounded-2xl mt-4 space-y-3.5">
                      <div className="flex items-center justify-between">
                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center">
                          <i className="fas fa-truck-fast text-emerald-400 mr-2"></i>
                          Opção de Envio ({activeStoreGroup.name})
                        </h4>
                        <span className="text-[9px] bg-emerald-500/10 text-emerald-400 px-2.5 py-0.5 rounded-full border border-emerald-500/20 font-bold uppercase">
                          Retirada Balcão
                        </span>
                      </div>
                      <div className="p-3.5 rounded-xl border border-emerald-500/40 bg-emerald-500/5 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                            <i className="fas fa-shop"></i>
                          </div>
                          <div>
                            <p className="text-xs font-bold text-white uppercase tracking-wider">Retirada no Balcão da Loja</p>
                            <p className="text-[10px] text-slate-400">Retirada imediata sem custo de entrega</p>
                          </div>
                        </div>
                        <span className="text-xs font-black text-emerald-400 font-mono">Grátis (R$ 0,00)</span>
                      </div>
                    </div>
                  ) : currentDeliveryMode === 'pickup' ? (
                    /* PADRÃO: RETIRADA NA LOJA */
                    <div className="bg-slate-950/40 border border-slate-800 p-4 sm:p-5 rounded-2xl mt-4 space-y-3.5 sm:space-y-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center space-x-2 min-w-0">
                          <i className="fas fa-store text-emerald-400 text-sm shrink-0"></i>
                          <h4 className="text-xs font-black text-white uppercase tracking-wider truncate">
                            Forma de Entrega: Retirada na Loja
                          </h4>
                        </div>
                        <span className="text-[10px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2.5 py-0.5 rounded-full font-bold uppercase shrink-0">
                          Padrão • Retirada
                        </span>
                      </div>

                      <div className="bg-emerald-950/20 border border-emerald-500/30 p-3.5 sm:p-4 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center space-x-2">
                            <span className="font-bold text-white">{activeStoreGroup.name}</span>
                            <span className="text-[10px] font-mono text-emerald-400 font-bold">R$ 0,00</span>
                          </div>
                          <p className="text-slate-400 text-[11px] leading-relaxed">
                            {getStorePhysicalAddress(storesDetailedInfo[activeStoreGroup.id.toString()])}
                          </p>
                          <p className="text-slate-500 text-[10px] flex items-center gap-1">
                            <i className="fas fa-circle-check text-emerald-400 text-[9px]"></i>
                            Endereço dispensado. Retire diretamente no balcão da loja após a confirmação.
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            setStoreDeliveryModes(prev => ({
                              ...prev,
                              [activeStoreGroup.id.toString()]: 'delivery'
                            }));
                            const storeMethods = storesShippingMethods[activeStoreGroup.id.toString()] || shippingMethods;
                            const nonPickup = storeMethods.find(m => !isPickupMethod(m));
                            if (nonPickup && !selectedFreights[activeStoreGroup.id.toString()]) {
                              setSelectedShippingId(nonPickup.id);
                            }
                          }}
                          className="w-full sm:w-auto px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-purple-400 hover:text-purple-300 border border-purple-500/30 rounded-xl text-xs font-bold transition-colors flex items-center justify-center gap-2 shrink-0 cursor-pointer active:scale-98"
                        >
                          <i className="fas fa-truck-fast"></i>
                          <span>Mais opções de entrega</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* MAIS OPÇÕES DE ENTREGA: CALCULAR FRETE E ENDEREÇO */
                    <div className="bg-slate-950/40 border border-slate-800 p-4 sm:p-5 rounded-2xl mt-4 space-y-4">
                      <div className="flex items-center justify-between flex-wrap gap-2 pb-3 border-b border-slate-800">
                        <div className="flex items-center space-x-2">
                          <i className="fas fa-truck-fast text-purple-400 text-sm"></i>
                          <h4 className="text-xs font-black text-white uppercase tracking-wider">
                            Entrega em Domicílio
                          </h4>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setStoreDeliveryModes(prev => ({
                              ...prev,
                              [activeStoreGroup.id.toString()]: 'pickup'
                            }));
                            const storeMethods = storesShippingMethods[activeStoreGroup.id.toString()] || shippingMethods;
                            const pickup = storeMethods.find(isPickupMethod);
                            setSelectedShippingId(pickup?.id || 4);
                          }}
                          className="text-xs text-emerald-400 hover:text-emerald-300 font-bold flex items-center gap-1.5 transition-colors cursor-pointer py-1"
                        >
                          <i className="fas fa-store text-[11px]"></i>
                          <span>Voltar para Retirada</span>
                        </button>
                      </div>

                      {/* Endereço de Entrega */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-bold text-slate-300 uppercase tracking-wider text-[11px]">
                            Endereço de Entrega:
                          </span>
                          <div className="flex items-center space-x-2">
                            {addresses.length > 1 && (
                              <select
                                value={selectedAddressId || ''}
                                onChange={(e) => setSelectedAddressId(Number(e.target.value))}
                                className="bg-slate-900 border border-slate-800 rounded-lg px-2 py-1 text-[11px] text-white focus:outline-none focus:border-purple-500 font-bold"
                              >
                                {addresses.map((addr) => (
                                  <option key={`select-addr-${addr.id}`} value={addr.id}>
                                    {addr.street}, {addr.number} ({addr.cep})
                                  </option>
                                ))}
                              </select>
                            )}
                            <Link to="/perfil/enderecos" className="text-[10px] font-bold text-purple-400 hover:text-purple-300 uppercase tracking-widest">
                              Gerenciar
                            </Link>
                          </div>
                        </div>

                        {buyerAddress ? (
                          <div className="bg-slate-900/60 border border-slate-800 p-3.5 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
                            <div className="space-y-0.5">
                              <p className="font-bold text-white">
                                {buyerAddress.street}, {buyerAddress.number} {buyerAddress.complement && `- ${buyerAddress.complement}`}
                              </p>
                              <p className="text-slate-400 text-[11px]">
                                {buyerAddress.neighborhood}, {buyerAddress.city} - {buyerAddress.state}
                              </p>
                              <p className="font-mono text-slate-500 text-[10px]">CEP: {buyerAddress.cep}</p>
                            </div>
                            <button
                              onClick={handleCalculateAllFreights}
                              disabled={isLoadingFreight}
                              className="w-full sm:w-auto px-3.5 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-colors flex items-center justify-center gap-1.5 shrink-0 cursor-pointer active:scale-98"
                            >
                              {isLoadingFreight ? (
                                <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                              ) : (
                                <>
                                  <i className="fas fa-calculator text-[10px]"></i>
                                  <span>Calcular Frete</span>
                                </>
                              )}
                            </button>
                          </div>
                        ) : (
                          <div className="bg-slate-900/60 border border-slate-800 p-4 rounded-xl text-center space-y-2">
                            <p className="text-xs text-amber-400 font-bold">Nenhum endereço cadastrado</p>
                            <p className="text-[11px] text-slate-500">Cadastre um endereço para calcular os fretes disponíveis para seu CEP.</p>
                            <Link 
                              to="/perfil/enderecos" 
                              className="inline-flex bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold px-4 py-1.5 rounded-lg transition-colors"
                            >
                              Cadastrar Endereço
                            </Link>
                          </div>
                        )}

                        {isCartAltered && (
                          <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-start gap-2 text-xs">
                            <i className="fas fa-triangle-exclamation mt-0.5 shrink-0 text-amber-500"></i>
                            <div>
                              <p className="font-black uppercase text-[10px]">Carrinho Alterado</p>
                              <p className="text-slate-300 text-[11px] leading-normal">
                                Clique em <strong className="text-purple-400">"Calcular Frete"</strong> para atualizar os valores de envio.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Opções de Frete Calculadas */}
                      {freightResults[activeStoreGroup.id.toString()] ? (
                        <div className="space-y-2 pt-2 border-t border-slate-800/60">
                          <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">
                            Selecione uma transportadora:
                          </span>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            {getProcessedRatesForStore(activeStoreGroup.id.toString()).map((rate: any, rIdx: number) => {
                              const isSelected = selectedFreights[activeStoreGroup.id.toString()]?.id === rate.id;
                              const priceValue = rate.custom_price ?? rate.price;
                              return (
                                <button
                                  key={`rate-${activeStoreGroup.id}-${rate.id || rIdx}`}
                                  disabled={!rate.isSelectable}
                                  onClick={() => {
                                    if (!rate.isSelectable) return;
                                    setSelectedFreights(prev => ({
                                      ...prev,
                                      [activeStoreGroup.id.toString()]: rate
                                    }));
                                    
                                    const storeMethods = storesShippingMethods[activeStoreGroup.id.toString()] || [];
                                    const matchingMethod = storeMethods.find(m => {
                                      if (rate.isManual) {
                                        return m.id === rate.id;
                                      }
                                      return Number(m.external_id) === Number(rate.id);
                                    });
                                    if (matchingMethod) {
                                      setSelectedShippingId(matchingMethod.id);
                                    }
                                  }}
                                  className={`flex flex-col justify-between p-3 rounded-xl border text-left transition-colors relative overflow-hidden cursor-pointer ${
                                    isSelected 
                                      ? 'bg-purple-950/40 border-purple-500 text-white' 
                                      : rate.isSelectable
                                        ? 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-white'
                                        : 'bg-slate-950/25 border-slate-900/60 text-slate-600 opacity-60 cursor-not-allowed'
                                  }`}
                                >
                                  <div className="flex items-start justify-between w-full mb-1">
                                    <div className="flex items-center space-x-1.5">
                                      {rate.company?.picture ? (
                                        <img 
                                          src={rate.company?.picture} 
                                          alt={rate.company?.name || "Carrier"} 
                                          className="h-3.5 w-auto object-contain bg-slate-900 border border-slate-800 rounded p-0.5" 
                                          onError={(e) => { (e.target as any).style.display = 'none'; }}
                                        />
                                      ) : (
                                        <div className="w-4 h-4 rounded bg-slate-800 flex items-center justify-center text-[8px] text-slate-400 font-bold">
                                          <i className="fas fa-truck text-[8px]"></i>
                                        </div>
                                      )}
                                      <span className="text-[10px] font-bold uppercase tracking-wider truncate">{rate.name}</span>
                                    </div>
                                    {rate.isSelectable && (
                                      <div className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0 ${
                                        isSelected ? 'border-purple-400 bg-purple-400' : 'border-slate-800'
                                      }`}>
                                        {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-slate-950" />}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-baseline justify-between w-full mt-2 pt-2 border-t border-slate-800/50 text-[10px]">
                                    <span className="text-slate-500 uppercase text-[9px]">Prazo: {rate.delivery_time}d</span>
                                    <span className={`font-bold ${rate.isSelectable ? 'text-emerald-400' : 'text-slate-600'}`}>
                                      R$ {Number(priceValue).toFixed(2)}
                                    </span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 bg-slate-900/40 border border-slate-800 border-dashed rounded-xl text-center space-y-1">
                          <i className="fas fa-truck-fast text-slate-600 text-lg"></i>
                          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Aguardando cotação</p>
                          <p className="text-[11px] text-slate-500">Clique em "Calcular Frete" acima para cotar os valores e prazos dos Correios e transportadoras.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment Selection */}
                  {isOrderingFromOwnStore ? (
                    <div className="bg-slate-950/20 border border-emerald-500/20 p-4 sm:p-5 rounded-2xl space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center">
                          <i className="fas fa-money-bill-wave text-emerald-400 mr-2"></i>
                          Forma de Pagamento ({activeStoreGroup.name})
                        </h4>
                        <span className="text-[9px] bg-emerald-500/10 text-emerald-400 px-2.5 py-0.5 rounded-full border border-emerald-500/20 font-bold uppercase">
                          Apenas Dinheiro
                        </span>
                      </div>
                      <div className="p-3.5 rounded-xl border border-emerald-500/40 bg-emerald-500/5 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                            <i className="fas fa-money-bill-1-wave"></i>
                          </div>
                          <div>
                            <p className="text-xs font-bold text-white uppercase tracking-wider">Pagamento em Dinheiro</p>
                            <p className="text-[10px] text-slate-400">Acerto presencial em espécie no balcão da sua loja</p>
                          </div>
                        </div>
                        <div className="w-4 h-4 rounded-full border border-emerald-400 bg-emerald-400 flex items-center justify-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-slate-950"></div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-950/20 border border-slate-800/60 p-4 sm:p-5 rounded-2xl space-y-3.5 sm:space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center">
                          <i className="fas fa-credit-card text-purple-400 mr-2"></i>
                          Escolher Forma de Pagamento
                        </h4>
                        {isCartTotalZero && (
                          <span className="text-[9px] bg-amber-500/10 text-amber-400 px-2.5 py-0.5 rounded-full border border-amber-500/20 font-bold uppercase tracking-wider">
                            Total R$ 0,00 • Apenas Dinheiro
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {effectivePaymentMethods.length > 0 ? (
                          effectivePaymentMethods.map((method, idx) => {
                            const isSelected = selectedPaymentId === method.id;
                            const isCash = isCashMethod(method);
                            return (
                              <button
                                key={`page-payment-${activeStoreGroup.id}-${method.id || idx}`}
                                onClick={() => setSelectedPaymentId(method.id)}
                                className={`p-3 sm:p-4 rounded-xl border text-left transition-colors relative flex items-start space-x-3 group cursor-pointer active:scale-98 ${
                                  isSelected
                                    ? 'bg-purple-950/40 border-purple-500 text-white'
                                    : 'bg-slate-950/40 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-white'
                                }`}
                              >
                                <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                  isSelected ? 'border-purple-400 bg-purple-400' : 'border-slate-800'
                                }`}>
                                  {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-slate-950" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-bold uppercase tracking-wider">{method.name}</p>
                                  {method.description && (
                                    <p className="text-[10px] text-slate-500 leading-normal mt-0.5 normal-case tracking-normal">
                                      {method.description}
                                    </p>
                                  )}
                                </div>
                                <i className={`fas ${
                                  method.type === 'pix' || method.slug === 'pix' 
                                    ? 'fa-brands fa-pix text-emerald-400/80 group-hover:text-emerald-400' 
                                    : isCash
                                      ? 'fa-money-bill-1-wave text-amber-400/80 group-hover:text-amber-400'
                                      : 'fa-credit-card text-purple-400/80 group-hover:text-purple-400'
                                } text-sm mt-0.5 transition-colors shrink-0`}></i>
                              </button>
                            );
                          })
                        ) : (
                          <div className="p-4 text-center bg-slate-950/20 rounded-xl border border-slate-800 border-dashed col-span-1 sm:col-span-2 text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                            Nenhuma forma de pagamento cadastrada ou disponível para esta loja
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Desktop Summary Card */}
          <div className="space-y-6">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl sm:rounded-3xl p-5 sm:p-6 space-y-6 sticky top-24">
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Resumo do Pedido</h2>
                {activeStoreGroup && (
                  <p className="text-[10px] text-purple-400 font-black uppercase tracking-widest leading-none">
                    Loja: {activeStoreGroup.name}
                  </p>
                )}
              </div>

              <div className="space-y-4 pt-4 border-t border-slate-800">
                {activeStoreGroup ? (
                  <>
                    <div className="flex justify-between text-slate-400 text-sm">
                      <span>Itens ({activeStoreGroup.items.length})</span>
                      <span className="font-mono">R$ {activeStoreGroup.total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-slate-400 text-sm">
                      <span>Frete</span>
                      <span className="text-emerald-400 font-bold">
                        {currentDeliveryMode === 'pickup' || isOrderingFromOwnStore 
                          ? 'Retirada' 
                          : selectedShippingId 
                            ? `+ R$ ${selectedStoreFreightPrice.toFixed(2)}` 
                            : 'Calcule o frete'}
                      </span>
                    </div>
                    <div className="flex justify-between text-slate-400 text-sm">
                      <span>Forma de Pagamento</span>
                      <span className="text-purple-400 font-bold uppercase tracking-wider text-[11px] truncate max-w-[50%]">
                        {isOrderingFromOwnStore 
                          ? 'Dinheiro' 
                          : effectivePaymentMethods.find(m => m.id === selectedPaymentId)?.name || (isCartTotalZero ? 'Dinheiro' : 'Escolha forma')}
                      </span>
                    </div>
                    <div className="flex justify-between text-white font-black text-xl pt-4 border-t border-slate-800">
                      <span>Total do Pedido</span>
                      <span className="text-purple-400 font-extrabold font-mono">
                        R$ {(activeStoreGroup.total + (currentDeliveryMode === 'pickup' || isOrderingFromOwnStore ? 0 : selectedStoreFreightPrice)).toFixed(2)}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="py-4 text-center text-xs text-slate-500">
                    Selecione um item ou uma loja para ver o resumo.
                  </div>
                )}

                {storesInCart.length > 1 && activeStoreGroup && (
                  <div className="pt-3 border-t border-slate-800/60 space-y-1 text-[11px]">
                    <p className="text-[9px] text-slate-500 font-black uppercase tracking-widest flex items-center">
                      <i className="fas fa-circle-info mr-1 text-purple-400"></i>
                      Multi-Lojas Ativo
                    </p>
                    <p className="text-slate-500 leading-normal">
                      Existem outras lojas no seu carrinho que continuarão salvas para finalizar depois.
                    </p>
                    <div className="flex justify-between text-[11px] text-slate-400 bg-slate-950/40 p-2 rounded-xl border border-slate-800/40 font-mono mt-1">
                      <span>Restante do Carrinho:</span>
                      <span>R$ {(total - activeStoreGroup.total).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              <button 
                onClick={handleOpenCheckout}
                disabled={isCheckoutDisabled}
                className={`w-full text-white font-black py-3.5 rounded-xl transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 cursor-pointer active:scale-98 ${
                  !isOrderingFromOwnStore && currentDeliveryMode === 'delivery' && isCartAltered
                    ? 'bg-rose-950/40 border border-rose-500/30 text-rose-400 font-bold disabled:cursor-not-allowed'
                    : !isOrderingFromOwnStore && isActiveStoreSalesDisabled
                      ? 'bg-rose-950/40 border border-rose-500/30 text-rose-400 disabled:cursor-not-allowed font-bold'
                      : isOrderingFromOwnStore
                        ? 'bg-emerald-600 hover:bg-emerald-500'
                        : 'bg-purple-600 hover:bg-purple-500'
                }`}
              >
                <span>
                  {isProcessing 
                    ? 'Processando...' 
                    : !isOrderingFromOwnStore && currentDeliveryMode === 'delivery' && isCartAltered 
                      ? 'Recalcule o frete para finalizar' 
                      : !isOrderingFromOwnStore && isActiveStoreSalesDisabled
                        ? 'Vendas Desabilitadas'
                        : isOrderingFromOwnStore
                          ? 'Finalizar Pedido Balcão'
                            : 'Finalizar Pedido'
                  }
                </span>
                {!isProcessing && (!isCartAltered || isOrderingFromOwnStore || currentDeliveryMode === 'pickup') && (!isActiveStoreSalesDisabled || isOrderingFromOwnStore) && <i className="fas fa-arrow-right text-xs"></i>}
                {!isProcessing && !isOrderingFromOwnStore && currentDeliveryMode === 'delivery' && isCartAltered && <i className="fas fa-arrows-rotate text-xs animate-spin"></i>}
                {!isOrderingFromOwnStore && isActiveStoreSalesDisabled && <i className="fas fa-ban text-xs text-rose-400"></i>}
              </button>

              {/* Warnings nested nicely below the button for extreme clarity */}
              {!isOrderingFromOwnStore && (
                isActiveStoreSalesDisabled ||
                (!selectedPaymentId && !isCartTotalZero) ||
                (currentDeliveryMode === 'delivery' && (!selectedAddressId || !selectedShippingId || isCartAltered))
              ) && (
                <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/10 space-y-1.5 text-left text-[10px] text-amber-500/90 [text-wrap:balance]">
                  {isActiveStoreSalesDisabled && (
                    <p className="text-rose-400">⚠️ Esta loja está com as vendas temporariamente desabilitadas.</p>
                  )}
                  {currentDeliveryMode === 'delivery' && isCartAltered && (
                    <div className="flex items-start gap-1.5 text-rose-400 bg-rose-500/10 p-2.5 rounded-lg border border-rose-500/10 mb-1.5">
                      <i className="fas fa-triangle-exclamation text-rose-400 shrink-0 mt-0.5"></i>
                      <div>
                        <p className="uppercase tracking-wider text-[9px] font-black">Recalcule o Frete</p>
                        <p className="font-medium text-[10px] text-slate-300 leading-normal">O carrinho mudou! Por favor, clique em <strong className="text-purple-400">"Calcular Frete"</strong> para recalcular novos prazos e opções de entrega.</p>
                      </div>
                    </div>
                  )}
                  {currentDeliveryMode === 'delivery' && !selectedAddressId && <p>⚠️ Cadastre e selecione um endereço de entrega.</p>}
                  {currentDeliveryMode === 'delivery' && selectedAddressId && !selectedShippingId && !isCartAltered && <p>⚠️ Clique em "Calcular Frete" e selecione uma opção de Envio.</p>}
                  {!selectedPaymentId && !isCartTotalZero && <p>⚠️ Selecione uma Forma de Pagamento para esta loja.</p>}
                </div>
              )}

              <Link to="/produtos" className="block text-center text-xs font-bold text-slate-500 hover:text-white transition-colors">
                Continuar comprando
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-slate-900/50 border border-slate-800 border-dashed rounded-3xl p-12 sm:p-24 text-center space-y-6">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-800 rounded-full flex items-center justify-center mx-auto text-slate-600 text-2xl sm:text-3xl">
            <i className="fas fa-shopping-cart"></i>
          </div>
          <div className="space-y-2">
            <h2 className="text-lg sm:text-xl font-bold text-white">Seu carrinho está vazio</h2>
            <p className="text-slate-500 text-xs sm:text-sm max-w-xs mx-auto">Explore o marketplace e encontre as melhores cartas e produtos para sua coleção.</p>
          </div>
          <Link 
            to="/produtos" 
            className="inline-flex bg-purple-600 hover:bg-purple-500 text-white font-bold px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl transition-colors text-xs sm:text-sm"
          >
            Ver Produtos
          </Link>
        </div>
      )}

      {/* Mobile Sticky Bottom Summary & Checkout Bar */}
      {cart.length > 0 && activeStoreGroup && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-slate-950/95 backdrop-blur-md border-t border-slate-800/90 px-4 py-3 shadow-[0_-8px_25px_rgba(0,0,0,0.5)]">
          <div className="flex items-center justify-between gap-3 max-w-lg mx-auto">
            <div className="flex flex-col min-w-0">
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold truncate">
                {currentDeliveryMode === 'pickup' || isOrderingFromOwnStore ? 'Total (Retirada)' : 'Total do Pedido'}
              </span>
              <div className="flex items-baseline gap-1.5">
                <span className="text-lg font-black text-purple-400 font-mono tracking-tight">
                  R$ {(activeStoreGroup.total + (currentDeliveryMode === 'pickup' || isOrderingFromOwnStore ? 0 : selectedStoreFreightPrice)).toFixed(2)}
                </span>
                {currentDeliveryMode === 'pickup' && (
                  <span className="text-[9px] text-emerald-400 font-bold uppercase tracking-wider">Retirada</span>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={handleOpenCheckout}
              disabled={isCheckoutDisabled}
              className={`px-5 py-2.5 sm:py-3 rounded-xl font-black text-xs transition-all duration-150 flex items-center justify-center space-x-2 shrink-0 cursor-pointer active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${
                !isOrderingFromOwnStore && currentDeliveryMode === 'delivery' && isCartAltered
                  ? 'bg-rose-950/40 border border-rose-500/30 text-rose-400'
                  : !isOrderingFromOwnStore && isActiveStoreSalesDisabled
                    ? 'bg-rose-950/40 border border-rose-500/30 text-rose-400'
                    : isOrderingFromOwnStore
                      ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-950/40'
                      : 'bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-950/40'
              }`}
            >
              <span>
                {isProcessing
                  ? 'Aguarde...'
                  : !isOrderingFromOwnStore && currentDeliveryMode === 'delivery' && isCartAltered
                    ? 'Recalcule o frete'
                    : !isOrderingFromOwnStore && isActiveStoreSalesDisabled
                      ? 'Vendas Pausadas'
                      : 'Finalizar Pedido'}
              </span>
              {!isProcessing && (!isCartAltered || isOrderingFromOwnStore || currentDeliveryMode === 'pickup') && (!isActiveStoreSalesDisabled || isOrderingFromOwnStore) && (
                <i className="fas fa-arrow-right text-[10px]"></i>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={isModalOpen}
        onClose={() => !isProcessing && setIsModalOpen(false)}
        activeStoreGroup={activeStoreGroup}
        addresses={addresses}
        selectedAddressId={selectedAddressId}
        shippingMethods={shippingMethods}
        selectedShippingId={selectedShippingId}
        paymentMethods={effectivePaymentMethods}
        selectedPaymentId={selectedPaymentId || (isCartTotalZero ? (effectivePaymentMethods.find(isCashMethod)?.id || effectivePaymentMethods[0]?.id || 4) : null)}
        selectedStoreFreightPrice={currentDeliveryMode === 'pickup' ? 0 : selectedStoreFreightPrice}
        processedRates={selectedStoreId ? getProcessedRatesForStore(selectedStoreId.toString()) : []}
        hasDocument={hasDocument}
        setHasDocument={setHasDocument}
        isOrderingFromOwnStore={isOrderingFromOwnStore}
        myStore={myStore}
        deliveryMode={currentDeliveryMode}
        storeInfo={selectedStoreId ? storesDetailedInfo[selectedStoreId.toString()] : null}
        onConfirmOrder={handleProcessCheckout}
        isProcessing={isProcessing}
      />
    </div>
  );
};
